import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { VisaTypeDialog } from '@/components/visas/VisaTypeDialog';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { VisaTypeCard } from '@/components/visas/VisaTypeCard';
interface VisaType {
  id: string;
  name_ar: string;
  name_en: string;
  created_at: string;
  updated_at: string;
}
export default function VisaTypes() {
  const [visaTypes, setVisaTypes] = useState<VisaType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVisaType, setEditingVisaType] = useState<VisaType | null>(null);
  const [view, setView] = useState<'table' | 'cards'>('table');
  const {
    toast
  } = useToast();
  const fetchVisaTypes = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('visa_types').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setVisaTypes(data || []);
    } catch (error) {
      console.error('Error fetching visa types:', error);
      toast({
        title: "خطأ",
        description: "فشل في تحميل أنواع التأشيرات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا النوع؟')) return;
    try {
      const {
        error
      } = await supabase.from('visa_types').delete().eq('id', id);
      if (error) throw error;
      toast({
        title: "تم الحذف",
        description: "تم حذف نوع التأشيرة بنجاح"
      });
      fetchVisaTypes();
    } catch (error) {
      console.error('Error deleting visa type:', error);
      toast({
        title: "خطأ",
        description: "فشل في حذف نوع التأشيرة",
        variant: "destructive"
      });
    }
  };
  const handleEdit = (visaType: VisaType) => {
    setEditingVisaType(visaType);
    setDialogOpen(true);
  };
  const handleAdd = () => {
    setEditingVisaType(null);
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingVisaType(null);
    fetchVisaTypes();
  };
  useEffect(() => {
    fetchVisaTypes();
  }, []);
  if (loading) {
    return <div className="flex items-center justify-center h-96">
        <div className="text-lg">جاري التحميل...</div>
      </div>;
  }
  return <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold">أنواع التأشيرات</h1>
        
        <ViewToggle view={view} onViewChange={v => setView(v as 'table' | 'cards')} />
        
        <Button onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          إضافة نوع تأشيرة
        </Button>
      </div>

      {view === 'table' ? <Card>
          <CardHeader>
            <CardTitle>قائمة أنواع التأشيرات</CardTitle>
            <CardDescription>
              عدد أنواع التأشيرات: {visaTypes.length}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">الاسم بالعربية</TableHead>
                  <TableHead className="text-right">الاسم بالإنجليزية</TableHead>
                  <TableHead className="text-right">تاريخ الإنشاء</TableHead>
                  <TableHead className="text-right">العمليات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visaTypes.map(visaType => <TableRow key={visaType.id}>
                    <TableCell className="font-medium">{visaType.name_ar}</TableCell>
                    <TableCell>{visaType.name_en}</TableCell>
                    <TableCell>
                      {new Date(visaType.created_at).toLocaleDateString('ar-SA')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(visaType)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(visaType.id)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </CardContent>
        </Card> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visaTypes.map(visaType => <VisaTypeCard key={visaType.id} visaType={visaType} onEdit={() => handleEdit(visaType)} onDelete={() => handleDelete(visaType.id)} />)}
        </div>}

      <VisaTypeDialog open={dialogOpen} onOpenChange={setDialogOpen} visaType={editingVisaType} onClose={handleDialogClose} />
    </div>;
}